// src/pages/Settings.js
import React, { useState } from 'react'
import {
    Box,
    Container,
    Card,
    CardContent,
    Typography,
    Grid,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    Divider,
    Switch,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Chip,
    Alert,
    ToggleButton,
    ToggleButtonGroup,
} from '@mui/material'
import {
    Notifications,
    Security,
    Language,
    Palette,
    Payment,
    PrivacyTip,
    Help,
    Logout,
    Delete,
    Email,
    DarkMode,
    LightMode,
    AutoMode,
    PhoneAndroid,
    CreditCard,
    AccountBalance,
    LocationOn,
    AccessTime,
    Shield,
    GppGood,
    Warning,
    Save as SaveIcon,
} from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import AnimatedSection from '../components/common/AnimatedSection'
import { useAuth } from '../contexts/AuthContext'
import { useTheme } from '../contexts/ThemeContext'

const Settings = () => {
    const navigate = useNavigate()
    const { user, logout } = useAuth()
    const { themeMode, setThemeMode } = useTheme()

    const [deleteDialog, setDeleteDialog] = useState(false)
    const [passwordDialog, setPasswordDialog] = useState(false)
    const [alert, setAlert] = useState(null)

    const [notificationSettings, setNotificationSettings] = useState({
        email: true,
        sms: true,
        push: true,
        marketing: false,
        transactions: true,
        security: true,
    })

    const [privacySettings, setPrivacySettings] = useState({
        location: true,
        analytics: true,
        personalizedAds: false,
        dataSharing: false,
    })

    const settingsMenu = [
        {
            category: 'Notifications',
            icon: Notifications,
            items: [
                { id: 'email', label: 'Email Notifications', description: 'Receive updates via email' },
                { id: 'sms', label: 'SMS Notifications', description: 'Receive SMS alerts for transactions' },
                { id: 'push', label: 'Push Notifications', description: 'Get notified on your device' },
                { id: 'marketing', label: 'Marketing Communications', description: 'Promotional offers and updates' },
                { id: 'transactions', label: 'Transaction Alerts', description: 'All transaction-related notifications' },
                { id: 'security', label: 'Security Alerts', description: 'Login attempts and security updates' },
            ],
        },
        {
            category: 'Privacy & Security',
            icon: PrivacyTip,
            items: [
                { id: 'location', label: 'Location Services', description: 'Allow location access for better service' },
                { id: 'analytics', label: 'Usage Analytics', description: 'Help improve our services' },
                { id: 'personalizedAds', label: 'Personalized Ads', description: 'Show relevant advertisements' },
                { id: 'dataSharing', label: 'Data Sharing', description: 'Share data with trusted partners' },
            ],
        },
    ]

    const handleNotificationToggle = (setting) => (event) => {
        setNotificationSettings({
            ...notificationSettings,
            [setting]: event.target.checked,
        })
    }

    const handlePrivacyToggle = (setting) => (event) => {
        setPrivacySettings({
            ...privacySettings,
            [setting]: event.target.checked,
        })
    }

    const handleSaveSettings = () => {
        setAlert({ type: 'success', message: 'Settings saved successfully!' })
    }

    const handleDeleteAccount = () => {
        setAlert({ type: 'info', message: 'Account deletion initiated. Please check your email.' })
        setDeleteDialog(false)
    }

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
            <Container maxWidth="lg" sx={{ py: 4 }}>
                <AnimatedSection>
                    <Box sx={{ mb: 4 }}>
                        <Typography variant="h4" gutterBottom>
                            Settings
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                            Customize your account preferences and privacy settings
                        </Typography>
                    </Box>

                    <Grid container spacing={3}>
                        {/* Account Settings */}
                        <Grid item xs={12} md={6}>
                            <Card>
                                <CardContent>
                                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <AccountBalance />
                                        Account Settings
                                    </Typography>

                                    <List>
                                        <ListItem divider>
                                            <ListItemIcon>
                                                <Language />
                                            </ListItemIcon>
                                            <ListItemText primary="Language" secondary="English (US)" />
                                            <Button size="small">Change</Button>
                                        </ListItem>

                                        <ListItem divider>
                                            <ListItemIcon>
                                                <Palette />
                                            </ListItemIcon>
                                            <ListItemText
                                                primary="Theme"
                                                secondary={`${themeMode.charAt(0).toUpperCase() + themeMode.slice(1)} Mode`}
                                            />
                                            <ToggleButtonGroup
                                                value={themeMode}
                                                exclusive
                                                onChange={(e, newMode) => newMode && setThemeMode(newMode)}
                                                size="small"
                                            >
                                                <ToggleButton value="light">
                                                    <LightMode />
                                                </ToggleButton>
                                                <ToggleButton value="dark">
                                                    <DarkMode />
                                                </ToggleButton>
                                                <ToggleButton value="auto">
                                                    <AutoMode />
                                                </ToggleButton>
                                            </ToggleButtonGroup>
                                        </ListItem>

                                        <ListItem divider>
                                            <ListItemIcon>
                                                <AccessTime />
                                            </ListItemIcon>
                                            <ListItemText primary="Time Zone" secondary="UTC-5 (Eastern Time)" />
                                            <Button size="small">Change</Button>
                                        </ListItem>

                                        <ListItem>
                                            <ListItemIcon>
                                                <LocationOn />
                                            </ListItemIcon>
                                            <ListItemText primary="Default Country" secondary="United States" />
                                            <Button size="small">Change</Button>
                                        </ListItem>
                                    </List>
                                </CardContent>
                            </Card>
                        </Grid>

                        {/* Payment Methods */}
                        <Grid item xs={12} md={6}>
                            <Card>
                                <CardContent>
                                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Payment />
                                        Payment Methods
                                    </Typography>

                                    <List>
                                        <ListItem divider>
                                            <ListItemIcon>
                                                <CreditCard />
                                            </ListItemIcon>
                                            <ListItemText primary="Primary Card" secondary="•••• •••• •••• 1234" />
                                            <Button size="small">Manage</Button>
                                        </ListItem>

                                        <ListItem divider>
                                            <ListItemIcon>
                                                <AccountBalance />
                                            </ListItemIcon>
                                            <ListItemText primary="Bank Account" secondary="Chase ••••5678" />
                                            <Button size="small">Manage</Button>
                                        </ListItem>

                                        <ListItem divider>
                                            <ListItemIcon>
                                                <PhoneAndroid />
                                            </ListItemIcon>
                                            <ListItemText primary="Mobile Money" secondary="+1 (555) 123-4567" />
                                            <Button size="small">Manage</Button>
                                        </ListItem>

                                        <ListItem>
                                            <Button fullWidth variant="outlined" startIcon={<Payment />}>
                                                Add New Payment Method
                                            </Button>
                                        </ListItem>
                                    </List>
                                </CardContent>
                            </Card>
                        </Grid>

                        {/* Notification Settings */}
                        <Grid item xs={12}>
                            <Card>
                                <CardContent>
                                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Notifications />
                                        Notification Preferences
                                    </Typography>

                                    <Grid container spacing={3}>
                                        {settingsMenu
                                            .find((cat) => cat.category === 'Notifications')
                                            ?.items.map((item) => (
                                                <Grid item xs={12} md={6} key={item.id}>
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 2 }}>
                                                        <Box>
                                                            <Typography variant="body1" fontWeight="medium">
                                                                {item.label}
                                                            </Typography>
                                                            <Typography variant="body2" color="text.secondary">
                                                                {item.description}
                                                            </Typography>
                                                        </Box>
                                                        <Switch checked={notificationSettings[item.id]} onChange={handleNotificationToggle(item.id)} />
                                                    </Box>
                                                </Grid>
                                            ))}
                                    </Grid>
                                </CardContent>
                            </Card>
                        </Grid>

                        {/* Privacy Settings */}
                        <Grid item xs={12}>
                            <Card>
                                <CardContent>
                                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Shield />
                                        Privacy & Security
                                    </Typography>

                                    <Grid container spacing={3}>
                                        {settingsMenu
                                            .find((cat) => cat.category === 'Privacy & Security')
                                            ?.items.map((item) => (
                                                <Grid item xs={12} md={6} key={item.id}>
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 2 }}>
                                                        <Box>
                                                            <Typography variant="body1" fontWeight="medium">
                                                                {item.label}
                                                            </Typography>
                                                            <Typography variant="body2" color="text.secondary">
                                                                {item.description}
                                                            </Typography>
                                                        </Box>
                                                        <Switch checked={privacySettings[item.id]} onChange={handlePrivacyToggle(item.id)} />
                                                    </Box>
                                                </Grid>
                                            ))}
                                    </Grid>
                                </CardContent>
                            </Card>
                        </Grid>

                        {/* Security Options */}
                        <Grid item xs={12} md={6}>
                            <Card>
                                <CardContent>
                                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Security />
                                        Security
                                    </Typography>

                                    <List>
                                        <ListItem divider>
                                            <ListItemIcon>
                                                <GppGood />
                                            </ListItemIcon>
                                            <ListItemText primary="Two-Factor Authentication" secondary="Add extra security to your account" />
                                            <Button size="small" onClick={() => setPasswordDialog(true)}>
                                                Manage
                                            </Button>
                                        </ListItem>

                                        <ListItem divider>
                                            <ListItemIcon>
                                                <PhoneAndroid />
                                            </ListItemIcon>
                                            <ListItemText primary="Trusted Devices" secondary="Manage your authorized devices" />
                                            <Button size="small">View</Button>
                                        </ListItem>

                                        <ListItem>
                                            <ListItemIcon>
                                                <AccessTime />
                                            </ListItemIcon>
                                            <ListItemText primary="Login History" secondary="View recent account access" />
                                            <Button size="small">View</Button>
                                        </ListItem>
                                    </List>
                                </CardContent>
                            </Card>
                        </Grid>

                        {/* Support & Help */}
                        <Grid item xs={12} md={6}>
                            <Card>
                                <CardContent>
                                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Help />
                                        Support & Help
                                    </Typography>

                                    <List>
                                        <ListItem divider>
                                            <ListItemIcon>
                                                <Email />
                                            </ListItemIcon>
                                            <ListItemText primary="Contact Support" secondary="Get help from our team" />
                                            <Button size="small" onClick={() => navigate('/contact')}>
                                                Contact
                                            </Button>
                                        </ListItem>

                                        <ListItem divider>
                                            <ListItemIcon>
                                                <Help />
                                            </ListItemIcon>
                                            <ListItemText primary="FAQ & Help Center" secondary="Find answers to common questions" />
                                            <Button size="small" onClick={() => navigate('/help-center')}>
                                                Visit
                                            </Button>
                                        </ListItem>

                                        <ListItem divider>
                                            <ListItemIcon>
                                                <Warning />
                                            </ListItemIcon>
                                            <ListItemText primary="Report Issue" secondary="Report a problem or bug" />
                                            <Button size="small" onClick={() => navigate('/contact')}>
                                                Report
                                            </Button>
                                        </ListItem>

                                        <ListItem>
                                            <ListItemIcon>
                                                <PrivacyTip />
                                            </ListItemIcon>
                                            <ListItemText primary="Privacy Policy" secondary="Learn how we protect your data" />
                                            <Button size="small" onClick={() => navigate('/privacy')}>
                                                Read
                                            </Button>
                                        </ListItem>
                                    </List>
                                </CardContent>
                            </Card>
                        </Grid>

                        {/* Account Actions */}
                        <Grid item xs={12}>
                            <Card>
                                <CardContent>
                                    <Typography variant="h6" gutterBottom color="error">
                                        Account Actions
                                    </Typography>

                                    <Grid container spacing={2}>
                                        <Grid item xs={12} md={6}>
                                            <Button
                                                fullWidth
                                                variant="outlined"
                                                color="error"
                                                startIcon={<Logout />}
                                                onClick={() => {
                                                    logout()
                                                    window.location.href = '/login'
                                                }}
                                            >
                                                Sign Out
                                            </Button>
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <Button
                                                fullWidth
                                                variant="outlined"
                                                color="error"
                                                startIcon={<Delete />}
                                                onClick={() => setDeleteDialog(true)}
                                            >
                                                Delete Account
                                            </Button>
                                        </Grid>
                                    </Grid>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>

                    {alert && (
                        <Alert severity={alert.type} onClose={() => setAlert(null)} sx={{ mt: 3 }}>
                            {alert.message}
                        </Alert>
                    )}

                    {/* Save Settings Button */}
                    <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                        <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSaveSettings}>
                            Save All Settings
                        </Button>
                    </Box>
                </AnimatedSection>

                {/* Password Change Dialog */}
                <Dialog open={passwordDialog} onClose={() => setPasswordDialog(false)} maxWidth="sm" fullWidth>
                    <DialogTitle>Change Password</DialogTitle>
                    <DialogContent>
                        <Box sx={{ mt: 2 }}>
                            <TextField fullWidth label="Current Password" type="password" sx={{ mb: 2 }} />
                            <TextField fullWidth label="New Password" type="password" sx={{ mb: 2 }} />
                            <TextField fullWidth label="Confirm New Password" type="password" />
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setPasswordDialog(false)}>Cancel</Button>
                        <Button variant="contained" color="primary">
                            Change Password
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Delete Account Dialog */}
                <Dialog open={deleteDialog} onClose={() => setDeleteDialog(false)} maxWidth="sm" fullWidth>
                    <DialogTitle>Delete Account</DialogTitle>
                    <DialogContent>
                        <Alert severity="error" sx={{ mb: 2 }}>
                            This action cannot be undone. All your data will be permanently deleted.
                        </Alert>
                        <Typography variant="body2" paragraph>
                            Are you sure you want to delete your account? This will:
                        </Typography>
                        <List dense>
                            <ListItem>
                                <ListItemText primary="Delete all your transaction history" />
                            </ListItem>
                            <ListItem>
                                <ListItemText primary="Remove all payment methods" />
                            </ListItem>
                            <ListItem>
                                <ListItemText primary="Close your wallet and transfer any remaining balance" />
                            </ListItem>
                            <ListItem>
                                <ListItemText primary="Permanently delete your personal information" />
                            </ListItem>
                        </List>
                        <TextField fullWidth label='Type "DELETE" to confirm' sx={{ mt: 2 }} />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setDeleteDialog(false)}>Cancel</Button>
                        <Button variant="contained" color="error" onClick={handleDeleteAccount}>
                            Delete Account
                        </Button>
                    </DialogActions>
                </Dialog>
            </Container>
        </Box>
    )
}

export default Settings
